import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth, useEffectiveUser } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { can, LEVEL_LABEL } from "../lib/permissions";
import { Avatar } from "./ui/Avatar";
import type { Employee } from "../lib/types";

// Full-screen iOS-style menu rendered when the beta-design flag is
// on inside the Capacitor iOS app. Replaces the dark off-canvas
// drawer used elsewhere with a system-style sheet:
//   - White / dark background that fills the screen
//   - Large "Menu" title at the top, Cancel button on the right
//   - Inset grouped list rows with chevrons
//   - Account block at the bottom with sign-out
//
// Triggered by the same `navOpen` flag the Layout already tracks
// for the standard drawer; the parent decides which menu to render
// based on `useBetaDesign()`.

interface Props {
  open: boolean;
  onClose: () => void;
}

export function IOSNativeMenu({ open, onClose }: Props) {
  const user = useAuth((s) => s.user);
  const effective = useEffectiveUser();
  const setUser = useAuth((s) => s.setUser);
  const qc = useQueryClient();
  const nav = useNavigate();
  const theme = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);

  const departments = useQuery({
    queryKey: ["departments"],
    queryFn: api.listDepartments,
  });
  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.listEmployees(),
  });
  const myEmployee: Employee | undefined =
    user?.employeeId
      ? employees.data?.find((e) => e.id === user.employeeId)
      : undefined;

  // Esc dismisses — matches iOS behavior of swipe-down on a sheet.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Each row gets the iOS inset-grouped look — rounded corners on
  // the first/last child via CSS. Tapping closes the menu and
  // navigates.
  function go(to: string) {
    onClose();
    nav(to);
  }

  const sections: Array<{
    label?: string;
    items: Array<{
      to: string;
      label: string;
      icon: string;
      guard?: boolean;
    }>;
  }> = [
    {
      items: [
        { to: "/", label: "Dashboard", icon: "▦" },
      ],
    },
    {
      label: "Schedules",
      items: (departments.data ?? []).map((d) => ({
        to: `/schedule/${d.id}`,
        label: d.name,
        icon: "▤",
      })),
    },
    {
      label: "Operations",
      items: [
        { to: "/employees", label: "Employees", icon: "◉" },
        { to: "/shifts", label: "Shifts", icon: "◷" },
        { to: "/time-off", label: "Time Off", icon: "✦" },
        { to: "/training", label: "Training", icon: "✦" },
        { to: "/on-call", label: "On-Call", icon: "☏" },
      ],
    },
    {
      label: "Admin",
      items: [
        { to: "/audit", label: "Audit", icon: "≡", guard: can.viewAudit(effective) },
      ],
    },
    // Settings sits in its own bottom-anchored section — mirrors
    // iOS's convention of dropping account / preference entries at
    // the very end of a menu, after the day-to-day nav.
    {
      items: [
        { to: "/settings", label: "Settings", icon: "⚙", guard: can.viewSettings(effective) },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-100 dark:bg-slate-950 flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* iOS-style large-title nav bar with Cancel on the right. */}
      <header className="px-4 pt-2 pb-3 flex items-end justify-between gap-2">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">
          Menu
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-[17px] text-sky-600 dark:text-sky-400 font-normal py-1 -mr-1 px-1"
        >
          Done
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-6">
        {sections.map((section, sIdx) => {
          const items = section.items.filter((it) => it.guard !== false);
          if (items.length === 0) return null;
          return (
            <section key={sIdx}>
              {section.label && (
                <h2 className="px-4 pb-1.5 text-[13px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
                  {section.label}
                </h2>
              )}
              <ul className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-700 shadow-sm">
                {items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      onClick={() => go(it.to)}
                      className={({ isActive }) =>
                        clsx(
                          "flex items-center gap-3 px-4 py-3 text-[17px] active:bg-slate-100 dark:active:bg-slate-700",
                          isActive
                            ? "text-sky-600 dark:text-sky-400"
                            : "text-slate-900 dark:text-slate-50",
                        )
                      }
                    >
                      <span className="w-6 text-center text-slate-400 dark:text-slate-500 text-sm">
                        {it.icon}
                      </span>
                      <span className="flex-1 truncate">{it.label}</span>
                      <span className="text-slate-300 dark:text-slate-600 text-lg leading-none">
                        ›
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {/* Theme toggle + account block. iOS apps put account-ish
          * actions in their own section near the bottom. */}
        <section>
          <ul className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-700 shadow-sm">
            <li>
              <button
                type="button"
                onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-4 py-3 text-[17px] text-slate-900 dark:text-slate-50 active:bg-slate-100 dark:active:bg-slate-700"
              >
                <span className="w-6 text-center text-slate-400 dark:text-slate-500 text-sm">
                  {theme === "dark" ? "☀" : "☾"}
                </span>
                <span className="flex-1 text-left">
                  {theme === "dark" ? "Day mode" : "Dark mode"}
                </span>
                <span className="text-slate-400 dark:text-slate-500 text-[15px]">
                  {theme === "dark" ? "Dark" : "Day"}
                </span>
              </button>
            </li>
          </ul>
        </section>

        {/* Account block — name, level, sign out. The Avatar +
          * profile-detail panel from the original sidebar isn't
          * here yet; for the beta, just the essentials. */}
        <section>
          {myEmployee && (
            <div className="flex items-center gap-3 px-4 pb-3">
              <Avatar
                photo={myEmployee.photo}
                firstName={myEmployee.firstName}
                lastName={myEmployee.lastName}
                size={48}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[17px] font-semibold text-slate-900 dark:text-slate-50 truncate">
                  {myEmployee.firstName} {myEmployee.lastName}
                </div>
                <div className="text-[13px] text-slate-500 dark:text-slate-400 truncate">
                  {user?.level ? LEVEL_LABEL[user.level] : ""}
                </div>
              </div>
            </div>
          )}
          <ul className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm">
            <li>
              <button
                type="button"
                onClick={async () => {
                  onClose();
                  await api.logout();
                  setUser(null);
                  qc.clear();
                }}
                className="w-full text-center px-4 py-3 text-[17px] text-red-600 active:bg-slate-100 dark:active:bg-slate-700"
              >
                Sign out
              </button>
            </li>
          </ul>
        </section>

        <div className="text-center text-[11px] text-slate-400 dark:text-slate-600 font-mono pt-2">
          build {__BUILD_SHA__}
        </div>
      </div>
    </div>
  );
}
